import type Dexie from 'dexie';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { isOfflineCapableNative } from '../../commons/utils/platform';
import { DexieBackend, SqliteBackend, StorageBackend, TABLE_KEYS, TableImpl } from './storage-backend';

// The app-facing offline store (OFFLINE_PLAN.md Phase 4, 2026-09-29).
//
// `offlineDb.<table>` keeps the exact shape the app already used with Dexie (get/put/where(...).equals(...)
// .sortBy(...)/filter(...).toArray() …) but runs on whichever backend was chosen at start-up — so no screen or
// service had to change. Every call waits for that choice (`storageReady`); every write also tells listeners
// (see watchOfflineRecord — the replacement for Dexie's liveQuery, which only works on IndexedDB).
//
// Backend choice (native app only — the website never stores anything offline):
//  - SQLite (SQLCipher-encrypted) when switched on: `environment.offlineSqlite === true`, or the tester switch
//    localStorage 'll_offline_sqlite' = '1';
//  - otherwise Dexie (IndexedDB) exactly as before.
// Moving a phone to SQLite happens once, at start-up, and ONLY when nothing is waiting to sync (outbox and
// pendingRows empty) — waiting work is never moved. Everything else (lists, tally rows, notes, the session
// snapshot) is copied over; cached photo/document files are not (they download again when opened). The move
// is recorded in the OLD store's never-wiped `notices` table, and from then on the phone always opens SQLite —
// even if the switch is later turned off — so it can never fall back to an empty old store while its real
// data sits in SQLite. If anything in the move fails, the phone simply stays on Dexie.

const SWITCHED_MARKER = '__offlineStorage';
const TESTER_SWITCH = 'll_offline_sqlite';

// ── change feed ─────────────────────────────────────────────────────────────────────────────────────────────
const listeners = new Map<string, Set<() => void>>();

function notify(table: string): void {
  listeners.get(table)?.forEach((fn) => { try { fn(); } catch { /* a listener's own problem */ } });
}

/** Emits `read()` now and again after every write to `table` — works on both backends (Dexie's liveQuery
 *  only watches IndexedDB). */
export function watchOfflineRecord<T>(table: string, read: () => Promise<T>): Observable<T> {
  return new Observable<T>((sub) => {
    let closed = false;
    const run = () => { read().then((v) => { if (!closed) sub.next(v); }).catch(() => undefined); };
    let set = listeners.get(table);
    if (!set) { set = new Set(); listeners.set(table, set); }
    set.add(run);
    run();
    return () => { closed = true; set!.delete(run); };
  });
}

// ── backend choice + one-time move ──────────────────────────────────────────────────────────────────────────
function sqliteSwitchedOn(): boolean {
  if ((environment as any).offlineSqlite === true) return true;
  try { return localStorage.getItem(TESTER_SWITCH) === '1'; } catch { return false; }
}

function containsBlob(v: unknown): boolean {
  if (v instanceof Blob) return true;
  if (Array.isArray(v)) return v.some(containsBlob);
  if (v && typeof v === 'object') return Object.values(v as Record<string, unknown>).some(containsBlob);
  return false;
}

/** Deletes the whole old IndexedDB database (not just its rows) so nothing readable stays behind. Best-effort. */
async function deleteOldDatabase(dexieDb: Dexie): Promise<boolean> {
  try {
    dexieDb.close();
    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(dexieDb.name);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('delete blocked'));
    });
    return true;
  } catch (err) {
    console.warn('[offline-store] old IndexedDB could not be deleted — it stays empty', String(err));
    return false;
  }
}

// "This phone has moved" is recorded INSIDE the encrypted database (never in IndexedDB, which is being retired).
// The database FILE alone proves nothing — it is also created when a move is postponed because changes are still
// waiting to sync — so only this record counts.
const MOVED_KEY = '__movedFromOldStore';

async function isMoved(sqlite: SqliteBackend): Promise<boolean> {
  return (await sqlite.table('notices').get(MOVED_KEY))?.value === '1';
}

async function markMoved(sqlite: SqliteBackend): Promise<void> {
  await sqlite.table('notices').put({ key: MOVED_KEY, value: '1' });
}

async function chooseBackend(dexieDb: Dexie): Promise<StorageBackend> {
  const dexie = new DexieBackend(dexieDb);
  if (!isOfflineCapableNative()) return dexie;

  // Already moved (record inside the encrypted database): open it WITHOUT touching IndexedDB — reading the old
  // store would re-create an empty IndexedDB database on every start.
  let sqlite: SqliteBackend | null = null;
  if (await SqliteBackend.exists()) {
    try {
      sqlite = await SqliteBackend.open();
      if (await isMoved(sqlite)) return sqlite;
    } catch (err) {
      console.warn('[offline-store] encrypted store exists but could not be read — using the old-store path', String(err));
      sqlite = null;
    }
  }

  let switchedBefore = false;
  try {
    switchedBefore = (await dexie.table('notices').get(SWITCHED_MARKER))?.value === 'sqlite';
  } catch { /* old store unreadable — treat as not switched */ }
  if (!switchedBefore && !sqliteSwitchedOn()) return dexie;

  if (!sqlite) {
    try {
      sqlite = await SqliteBackend.open();
    } catch (err) {
      console.warn('[offline-store] encrypted store could not open — staying on the current store', String(err));
      return dexie;
    }
  }

  if (switchedBefore) {
    // Moved by an earlier app version, which kept the marker in IndexedDB: record it inside the encrypted database
    // now, then the old (empty) IndexedDB can go. If recording fails, leave everything as it was.
    try {
      await markMoved(sqlite);
      await deleteOldDatabase(dexieDb);
    } catch (err) {
      console.warn('[offline-store] could not record the earlier move — old marker kept', String(err));
    }
    return sqlite;
  }

  // First time: move only when nothing is waiting to sync (waiting work is never moved).
  try {
    const waiting = (await dexie.table('outbox').count()) + (await dexie.table('pendingRows').count());
    if (waiting > 0) {
      console.info('[offline-store] changes still waiting to sync — moving to the encrypted store later');
      return dexie;
    }
    for (const name of Object.keys(TABLE_KEYS)) {
      if (name === 'outbox' || name === 'pendingRows') continue;
      const rows = (await dexie.table(name).toArray()).filter((r) => !containsBlob(r));
      await sqlite.importRows(name, rows);
      // Check the copy landed BEFORE the old data is touched: if anything is short, this throws and the phone
      // stays on the old store (the catch below), with nothing deleted.
      const stored = await sqlite.table(name).count();
      if (stored < rows.length) throw new Error(`copy check failed for ${name}: ${stored} of ${rows.length} rows`);
    }
    // Everything is copied and checked. Record the move inside the encrypted database — if THIS fails we are still
    // safe (nothing old was touched yet). From here on the phone is moved and nothing below may undo that.
    await markMoved(sqlite);
  } catch (err) {
    console.warn('[offline-store] move to the encrypted store failed — staying on the current store', String(err));
    try { await sqlite.clearAll(); } catch { /* leave it; it is not used */ }
    return dexie;
  }

  // Retire the old IndexedDB. Best-effort only: the encrypted copy is already verified and recorded.
  if (!(await deleteOldDatabase(dexieDb))) {
    try {
      await dexieDb.open(); // deleteOldDatabase() closed it
      for (const name of Object.keys(TABLE_KEYS)) {
        if (name !== 'notices') await dexie.table(name).clear();
      }
      await dexie.table('notices').clear();
      await dexie.table('notices').put({ key: SWITCHED_MARKER, value: 'sqlite' });
    } catch { /* a leftover old copy is harmless; the encrypted store is the one in use */ }
  }
  console.info('[offline-store] moved to the encrypted store');
  return sqlite;
}

// ── table facade ────────────────────────────────────────────────────────────────────────────────────────────
export class OfflineTable<T = any, K = any> {
  constructor(private readonly name: string, private readonly ready: Promise<StorageBackend>) {}

  private impl(): Promise<TableImpl> {
    return this.ready.then((b) => b.table(this.name));
  }

  private write<R>(fn: (t: TableImpl) => Promise<R>): Promise<R> {
    return this.impl().then(fn).then((r) => { notify(this.name); return r; });
  }

  get(key: K): Promise<T | undefined> { return this.impl().then((t) => t.get(key)); }
  put(obj: T): Promise<any> { return this.write((t) => t.put(obj)); }
  add(obj: T): Promise<any> { return this.write((t) => t.add(obj)); }
  delete(key: K): Promise<void> { return this.write((t) => t.delete(key)); }
  bulkDelete(keys: K[]): Promise<void> { return this.write((t) => t.bulkDelete(keys)); }
  update(key: K, changes: Partial<T> | Record<string, any>): Promise<number> {
    return this.write((t) => t.update(key, changes as Record<string, any>));
  }
  clear(): Promise<void> { return this.write((t) => t.clear()); }
  toArray(): Promise<T[]> { return this.impl().then((t) => t.toArray()); }
  count(): Promise<number> { return this.impl().then((t) => t.count()); }

  where(field: string) {
    const collection = (values: any[]) => ({
      toArray: (): Promise<T[]> => this.impl().then((t) => t.whereIn(field, values)),
      sortBy: (by: string): Promise<T[]> => this.impl().then((t) => t.whereIn(field, values))
        .then((rows) => rows.sort((a: any, b: any) => (a?.[by] < b?.[by] ? -1 : a?.[by] > b?.[by] ? 1 : 0))),
      count: (): Promise<number> => this.impl().then((t) => t.whereInCount(field, values)),
      delete: (): Promise<number> => this.write((t) => t.whereInDelete(field, values)),
    });
    return {
      equals: (value: any) => collection([value]),
      anyOf: (...values: any[]) => collection(values.flat()),
      startsWith: (prefix: string) => {
        if (field !== TABLE_KEYS[this.name]) throw new Error(`startsWith is only supported on the primary key of ${this.name}`);
        return {
          toArray: (): Promise<T[]> => this.impl().then((t) => t.startsWith(prefix)),
          primaryKeys: (): Promise<K[]> => this.impl().then((t) => t.startsWithKeys(prefix)),
        };
      },
    };
  }

  orderBy(field: string) {
    return {
      toArray: (): Promise<T[]> => this.toArray()
        .then((rows) => rows.sort((a: any, b: any) => (a?.[field] < b?.[field] ? -1 : a?.[field] > b?.[field] ? 1 : 0))),
    };
  }

  filter(fn: (row: T) => boolean) {
    // Outbox rows come back with their real files (a queued photo is shown as a preview); other tables get
    // lightweight file placeholders — their filters only look at record fields or file sizes.
    const fullFiles = this.name === 'outbox';
    return { toArray: (): Promise<T[]> => this.impl().then((t) => t.filter(fn as (row: any) => boolean, fullFiles)) };
  }
}

/** Starts the backend choice once; every table call waits for it. */
export function createStorageReady(dexieDb: Dexie): Promise<StorageBackend> {
  return chooseBackend(dexieDb).catch(() => new DexieBackend(dexieDb));
}
